import type { Block } from '@w/content-blocks'
import { tableGrid } from '@w/content-blocks/src/table-html'
import type { CoreState } from './state'
import type { EditorIntent } from './intents'
import { moveBlock, replaceBlock, replaceChildren, setBlockProps, spliceBlocks } from './ops'
import { tableShape } from './table-grid'
import { children, pointAt, rows, type Row } from './tree-read'

/** Span-aware table intent, inside applyIntent's CRDT transaction and history boundary. */
export function editTable(state: CoreState, row: Row, control: Extract<EditorIntent, { type: 'tableControl' }>['control']): void {
  const table = row.parents.find(block => block.type === 'table')
  if (table?.type !== 'table') return
  const grid = tableGrid(table)
  const originY = grid.findIndex(line => line.some(cell => cell.id === row.block.id))
  const originX = grid[originY]?.findIndex(cell => cell.id === row.block.id) ?? -1
  if (originY < 0 || originX < 0) return
  const range = state.selection?.table
  const from = range ? tableShape(grid, range).from : { x: originX, y: originY }
  const to = range ? tableShape(grid, range).to : { x: originX + (grid[originY][originX].colSpan ?? 1) - 1,
    y: originY + (grid[originY][originX].rowSpan ?? 1) - 1 }
  const { x, y } = to
  const tableRows = table.children
  const cell = grid[from.y]?.[from.x]
  if (!cell) return
  if (!['add-row', 'add-column', 'delete-row', 'delete-column'].includes(control)) {
    if (control === 'header-row' || control === 'header-column') {
      const selected = control === 'header-row' ? grid[y] : grid.map(line => line[x])
      const header = !('_isHeader' in cell && cell._isHeader)
      for (const target of new Set(selected)) if (target) setBlockProps(state, target, { _isHeader: header || undefined })
    } else if (control === 'merge' && range) {
      const left = from.x
      const top = from.y
      const target = grid[top][left]
      const selected = new Set(grid.slice(top, y + 1).flatMap(line => line.slice(left, x + 1)))
      // Lexical $mergeCells keeps the upper-left cell and moves the selected cell contents into it.
      const content = [...target.children]
      for (const other of selected) if (other !== target) {
        if (content.length > 0 && other.children.length > 0) content.push({ id: '', type: 'br' })
        content.push(...other.children)
        for (const tr of tableRows) {
          const index = children(tr).indexOf(other)
          if (index >= 0) spliceBlocks(state, children(tr), index, 1)
        }
      }
      replaceChildren(state, target, content)
      setBlockProps(state, target, { colSpan: x > left ? x - left + 1 : undefined, rowSpan: y > top ? y - top + 1 : undefined })
      const point = pointAt(rows([target])[0], 0)
      state.selection = { anchor: point, focus: point }
      return
    } else if (control === 'unmerge') {
      const top = grid.findIndex(line => line.includes(cell))
      const left = grid[top].indexOf(cell)
      for (let yy = top; yy < top + (cell.rowSpan ?? 1); yy++) {
        for (let xx = left; xx < left + (cell.colSpan ?? 1); xx++) {
          if (yy === top && xx === left) continue
          const siblings = children(tableRows[yy])
          const next = grid[yy].slice(xx + 1).find(candidate => candidate !== cell && siblings.includes(candidate))
          spliceBlocks(state, siblings, next ? siblings.indexOf(next) : siblings.length, 0, { id: '', type: 'td', children: [] })
        }
      }
      setBlockProps(state, cell, { colSpan: undefined, rowSpan: undefined })
    }
    const point = pointAt(rows([cell])[0], 0)
    state.selection = { anchor: point, focus: point }
    return
  }
  const desiredRows: Block[] = [...table.children]
  const empty = (): Extract<Block, { type: 'td' }> => ({ id: '', type: 'td', children: [] })
  let columnEdit: { index: number; removed: number; added: number } | null = null
  if (control === 'add-row') {
    const count = to.y - from.y + 1
    const at = to.y + 1
    const added = Array.from({ length: count }, () => grid[0].map((_, x) => {
      const after = grid[at]?.[x], before = grid[at - 1]?.[x]
      return before && before === after ? before : empty()
    }))
    const newRows = added.map(() => ({ id: '', type: 'tr' as const, children: [] }))
    grid.splice(at, 0, ...added)
    desiredRows.splice(at, 0, ...newRows)
    spliceBlocks(state, table.children, at, 0, ...newRows)
  } else if (control === 'add-column') {
    const at = to.x + 1, count = to.x - from.x + 1
    for (const line of grid) {
      const after = line[at], before = line[at - 1]
      // Lexical $insertTableColumnAtSelection: a new cell in a header row is a header cell.
      const header = line.every(cell => '_isHeader' in cell && cell._isHeader)
      line.splice(at, 0, ...Array.from({ length: count }, () => before && before === after ? before
        : header ? Object.assign(empty(), { _isHeader: true }) : empty()))
    }
    columnEdit = { index: at, removed: 0, added: count }
  } else if (control === 'delete-row') {
    grid.splice(from.y, to.y - from.y + 1)
    desiredRows.splice(from.y, to.y - from.y + 1)
  } else {
    for (const line of grid) line.splice(from.x, to.x - from.x + 1)
    columnEdit = { index: from.x, removed: to.x - from.x + 1, added: 0 }
  }
  if (grid.length === 0 || grid[0].length === 0) {
    const paragraph: Block = { id: '', type: 'p', children: [] }
    replaceBlock(state, table, paragraph)
    const point = { blockId: paragraph.id, offset: 0 }
    state.selection = { anchor: point, focus: point }
    return
  }
  const origins = new Map<Extract<Block, { type: 'td' }>, { x: number; y: number; right: number; bottom: number }>()
  for (const [y, line] of grid.entries()) for (const [x, cell] of line.entries()) {
    const bounds = origins.get(cell)
    if (bounds) { bounds.right = Math.max(bounds.right, x); bounds.bottom = Math.max(bounds.bottom, y) }
    else origins.set(cell, { x, y, right: x, bottom: y })
  }
  const desired = desiredRows.map((_, y) => [...origins].filter(([, bounds]) => bounds.y === y)
    .toSorted(([, left], [, right]) => left.x - right.x).map(([cell]) => cell))
  for (const [y, cells] of desired.entries()) {
    const target = children(desiredRows[y])
    for (const [index, cell] of cells.entries()) {
      if (!cell.id) spliceBlocks(state, target, index, 0, cell)
      else if (target[index] !== cell) moveBlock(state, cell, target, index)
    }
  }
  for (let index = table.children.length - 1; index >= 0; index--) {
    const row = table.children[index]
    if (!desiredRows.includes(row)) { spliceBlocks(state, table.children, index, 1); continue }
    const cells = children(row)
    for (let x = cells.length - 1; x >= 0; x--) {
      const cell = cells[x]
      if (cell.type === 'td' && !origins.has(cell)) spliceBlocks(state, cells, x, 1)
    }
  }
  for (const [cell, bounds] of origins) {
    const colSpan = bounds.right - bounds.x + 1, rowSpan = bounds.bottom - bounds.y + 1
    if ((cell.colSpan ?? 1) !== colSpan || (cell.rowSpan ?? 1) !== rowSpan) {
      setBlockProps(state, cell, { colSpan: colSpan > 1 ? colSpan : undefined, rowSpan: rowSpan > 1 ? rowSpan : undefined })
    }
  }
  if (columnEdit) {
    const { index, removed, added } = columnEdit
    const patch: Record<string, unknown> = {}
    if (table._aligns) patch._aligns = table._aligns.toSpliced(index, removed, ...Array.from({ length: added }, () => ''))
    if (table._colWidths) patch._colWidths = table._colWidths.toSpliced(index, removed,
      ...Array.from({ length: added }, () => table._colWidths?.[Math.max(0, index - 1)] ?? 1))
    if (Object.keys(patch).length > 0) setBlockProps(state, table, patch)
  }
  const survivor = grid[Math.min(from.y, grid.length - 1)][Math.min(from.x, grid[0].length - 1)]
  const current = rows(state.blocks).find(entry => entry.block.id === survivor.id)
  if (current) { const point = pointAt(current, 0); state.selection = { anchor: point, focus: point } }
}
